import { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { AuthError } from '@atlaskit/outbound-auth-flow-client/error';

import { useSmartLinkConnectorByKey } from '@atlassian/confluence-account-setup/use-smartlink-connector-by-key';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getGdriveConnectClickedEvent,
	getGdriveConnectFailedEvent,
	getGdriveConnectSucceededEvent,
} from '../promoBannerAnalytics.ts';

export interface UseGdriveSmartLinkAuthResult {
	authUrl: string | undefined;
	isAuthenticated: boolean;
	isLoading: boolean;
	isConnecting: boolean;
	connect: () => Promise<void>;
}

export const useGdriveSmartLinkAuth = (): UseGdriveSmartLinkAuthResult => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isConnecting, setIsConnecting] = useState(false);

	const { authUrl, isAuthenticated, isLoading, onConnected } = useSmartLinkConnectorByKey({
		aaId: userId ?? undefined,
		productKey: ProductKeys3P.GoogleDrive,
	});

	const connect = useCallback(async () => {
		if (!authUrl || isConnecting) {
			return;
		}
		createAnalyticsEvent(getGdriveConnectClickedEvent()).fire();
		setIsConnecting(true);
		try {
			await auth(authUrl);
			createAnalyticsEvent(getGdriveConnectSucceededEvent()).fire();
			// Optimistically flip + force-refresh the shared outbound-auth cache
			// so the banner self-hides on the next render without a page reload.
			onConnected();
		} catch (err) {
			// Swallow (incl. user-cancelled AuthError) so the banner stays put and the
			// fire-and-forget onClick caller never sees an unhandled rejection.
			const reason = err instanceof AuthError ? (err.type ?? null) : null;
			createAnalyticsEvent(getGdriveConnectFailedEvent(reason)).fire();
		} finally {
			setIsConnecting(false);
		}
	}, [authUrl, isConnecting, onConnected, createAnalyticsEvent]);

	return { authUrl, isAuthenticated, isLoading, isConnecting, connect };
};
